import { Assistant, AssistantsService } from "@/generated";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { useMemo } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { useInfiniteQuery } from "@tanstack/react-query";
import { SpotSelect } from "@/components/Form/SelectField";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { NoxusIcon } from "../UI/NoxusIcon";

interface CoworkerOptionType {
  value: string;
  label: string;
  icon?: string | React.ReactNode;
  description?: string;
}

interface ICoworkerSelectFieldProps {
  name: string;
  placeholder?: string;
  label?: string;
  tooltip?: string;
  options?: CoworkerOptionType[];
  rules?: RegisterOptions;
  control: Control<any>;
  isClearable?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  onChange?: (value: string) => void;
  defaultValue?: string;
  includeCurrentNode?: boolean;
}

interface ICoworkerSelect {
  value?: string | CoworkerOptionType;
  onChange?: (value: any) => void;
  options: CoworkerOptionType[];
  placeholder?: string;
  label?: string;
  tooltip?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  defaultValue?: CoworkerOptionType;
  name?: string;
}

const CoworkerSelect = ({
  value,
  options,
  placeholder,
  label,
  tooltip,
  onChange,
  isLoading = false,
  isDisabled = false,
  isClearable = false,
  defaultValue,
  name,
}: ICoworkerSelect) => {
  const parsedValue =
    typeof value === "string" ? options.find((o) => o.value === value) : value;

  return (
    <div className="w-full">
      {label && <NoxusFormLabel label={label} tooltip={tooltip} name={name} />}
      <SpotSelect
        value={parsedValue}
        label=""
        options={options}
        tooltip={tooltip}
        placeholder={placeholder}
        isDisabled={isDisabled}
        defaultValue={defaultValue}
        isLoading={isLoading}
        isClearable={isClearable}
        onChange={onChange}
      />
    </div>
  );
};

export const CoworkerSelectField = (props: ICoworkerSelectFieldProps) => {
  const { spotGroup } = useSpotUser();

  const { data: assistantsData, isLoading: isLoadingAssistants } =
    useInfiniteQuery({
      queryKey: [DATA_KEYS.ASSISTANTS],
      queryFn: ({ pageParam = 1 }) =>
        AssistantsService.getAssistants({
          path: {
            group_id: spotGroup.id,
          },
          query: {
            page: pageParam,
            size: 50,
          },
        }),
      staleTime: 3000,
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.data || !lastPage.data.page || !lastPage.data.pages)
          return undefined;
        return lastPage.data.page < lastPage.data.pages
          ? allPages.length + 1
          : undefined;
      },
    });

  const assistantOptions = useMemo(() => {
    const assistants =
      assistantsData?.pages?.flatMap((page) => page.data.items) || [];

    return assistants.map(
      (assistant: Assistant): CoworkerOptionType => ({
        value: assistant.id,
        label: assistant.name,
        icon: <NoxusIcon icon={assistant.image_url || "coworker"} />,
        description: assistant.description || undefined,
      })
    );
  }, [assistantsData]);

  const CURRENT_NODE_VALUE = "__current_node__";

  const baseOptions = props.options || assistantOptions;
  const options = useMemo(() => {
    if (props.includeCurrentNode) {
      const currentNodeOption: CoworkerOptionType = {
        value: CURRENT_NODE_VALUE,
        label: "Current Node",
        icon: <NoxusIcon icon="node" />,
        description: "Use the settings configured in this node",
      };
      return [currentNodeOption, ...baseOptions];
    }
    return baseOptions;
  }, [baseOptions, props.includeCurrentNode]);
  const isLoading = props.isLoading || isLoadingAssistants;

  // Convert null/undefined to CURRENT_NODE_VALUE for display when includeCurrentNode is enabled
  const toDisplayValue = (val: string | null | undefined): string => {
    if (props.includeCurrentNode && (val === null || val === undefined)) {
      return CURRENT_NODE_VALUE;
    }
    return val || "";
  };

  // Convert CURRENT_NODE_VALUE back to null for storage
  const toStorageValue = (val: string): string | null => {
    if (props.includeCurrentNode && val === CURRENT_NODE_VALUE) {
      return null;
    }
    return val;
  };

  const defaultValue = options.find(
    (option) => option.value === toDisplayValue(props.defaultValue)
  );

  return (
    <Controller
      control={props.control}
      name={props.name}
      defaultValue={props.defaultValue}
      rules={props.rules}
      render={({ field: { onChange, value } }) => {
        const handleChange = (selectedValue: any) => {
          if (!selectedValue) {
            const storageVal = toStorageValue("");
            if (props.onChange) props.onChange(storageVal || "");
            onChange(storageVal);
            return;
          }
          const rawValue = Object.keys(selectedValue).includes("value")
            ? selectedValue?.value
            : selectedValue;
          const storageVal = toStorageValue(rawValue);
          onChange(storageVal);
          if (props.onChange) props.onChange(storageVal || "");
        };

        // Convert stored value to display value
        const displayValue = toDisplayValue(value);

        return (
          <CoworkerSelect
            value={displayValue}
            label={props.label}
            options={options}
            tooltip={props.tooltip}
            placeholder={props.placeholder}
            isDisabled={props.isDisabled}
            defaultValue={defaultValue}
            isLoading={isLoading}
            isClearable={props.isClearable}
            onChange={handleChange}
          />
        );
      }}
    />
  );
};
